import React, { useEffect, useState } from 'react';
import Seo from '../components/Seo';

const API = '/api/scienceCommunicatorVote';

// Fisher–Yates shuffle — randomises candidate order so no one gets a
// fixed-position advantage. Runs once per visit (per page load).
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Initials fallback for a finalist with no headshot on file.
function initials(name) {
  return (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

// Pull the YouTube video id out of any common share URL form.
function youtubeEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url.trim());
    let id = '';
    if (u.hostname.includes('youtu.be')) id = u.pathname.slice(1);
    else if (u.pathname.startsWith('/embed/')) id = u.pathname.split('/embed/')[1];
    else if (u.pathname.startsWith('/shorts/')) id = u.pathname.split('/shorts/')[1];
    else id = u.searchParams.get('v') || '';
    id = (id || '').split('/')[0].split('?')[0];
    return id ? `https://www.youtube.com/embed/${id}` : null;
  } catch (e) {
    return null;
  }
}

export default function ScienceCommunicatorAward() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasVoted, setHasVoted] = useState(false);
  // Why voting is closed for this visitor: 'voted' (they already voted from
  // this browser) or 'network' (someone on their network already voted).
  const [voteReason, setVoteReason] = useState(null);
  const [votedId, setVotedId] = useState(null);   // local-only, for the thank-you state
  const [pendingId, setPendingId] = useState(null); // candidate awaiting confirm
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Voting window — the server is authoritative. When closed it returns the
  // top-3 winners instead of the candidate grid.
  const [votingOpen, setVotingOpen] = useState(true);
  const [winners, setWinners] = useState([]);

  function loadState() {
    return fetch(API, { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((d) => {
        if (d.votingOpen === false) {
          setVotingOpen(false);
          setWinners(Array.isArray(d.winners) ? d.winners : []);
        } else {
          setVotingOpen(true);
          setCandidates(shuffle(Array.isArray(d.candidates) ? d.candidates : []));
          setHasVoted(!!d.hasVoted);
          setVoteReason(d.voteReason || null);
        }
      });
  }

  useEffect(() => {
    loadState()
      .catch(() => setError('Could not load the candidates. Please refresh.'))
      .finally(() => setLoading(false));
  }, []);

  async function castVote(id) {
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ candidateId: id }),
      });
      if (res.ok) {
        setHasVoted(true);
        setVotedId(id);
        setVoteReason('voted');
      } else if (res.status === 409) {
        // Already counted from this IP / browser — surface which one.
        const body = await res.json().catch(() => ({}));
        setHasVoted(true);
        setVoteReason(body.reason || 'voted');
      } else if (res.status === 403) {
        // Voting closed while this page was open — flip to the winners view.
        setPendingId(null);
        await loadState().catch(() => {});
      } else {
        setError('Something went wrong recording your vote. Please try again.');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
      setPendingId(null);
    }
  }

  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh', paddingBottom: 64 }}>
      <Seo
        title="Science Communicator Award"
        path="/science-communicator-award"
        description="Vote for the IRO Science Communicator Award — celebrating finalists who best communicate their research to the world."
      />
      <style>{`
        .scv-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 28px;
          max-width: 1180px;
          margin: 0 auto;
          padding: 0 24px;
        }
        @media (max-width: 980px) { .scv-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 640px) { .scv-grid { grid-template-columns: 1fr; } }
        .scv-card {
          background: #fff;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 4px 18px rgba(0,0,0,0.07);
          display: flex;
          flex-direction: column;
          border: 1px solid #eef0f3;
        }
        .scv-video { position: relative; width: 100%; padding-top: 56.25%; background: #0b1f3a; }
        .scv-video iframe, .scv-video .scv-soon {
          position: absolute; inset: 0; width: 100%; height: 100%; border: 0;
        }
        .scv-soon {
          display: flex; flex-direction: column; gap: 12px;
          align-items: center; justify-content: center;
          color: #aebfd6; font-weight: 600; letter-spacing: .02em;
        }
        .scv-soon-photo {
          width: 88px; height: 88px; border-radius: 50%; object-fit: cover;
          object-position: center top; border: 3px solid rgba(255,255,255,0.22);
        }
        .scv-soon-initials {
          display: flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.12); color: #dce7f5; font-size: 30px;
        }
        .scv-name-row { display: flex; align-items: center; gap: 12px; }
        .scv-avatar {
          width: 46px; height: 46px; border-radius: 50%; object-fit: cover;
          object-position: center top; flex: 0 0 auto; border: 1px solid #e3e8ef;
        }
        .scv-avatar-initials {
          display: flex; align-items: center; justify-content: center;
          background: #e8eef3; color: #004d99; font-size: 16px; font-weight: 700;
        }
        .scv-btn {
          appearance: none; border: 0; cursor: pointer;
          background: #004d99; color: #fff; font-weight: 700; font-size: 15px;
          padding: 12px 16px; border-radius: 9px; width: 100%;
          transition: background .15s ease;
        }
        .scv-btn:hover { background: #0066cc; }
        .scv-btn:disabled { background: #c7ced9; cursor: not-allowed; }
        .scv-btn.scv-confirm { background: #047a3d; }
        .scv-btn.scv-confirm:hover { background: #05924a; }
        .scv-btn.scv-cancel {
          background: transparent; color: #687587; border: 1px solid #cfd6e0;
        }
        .scv-btn.scv-cancel:hover { background: #eef0f3; }

        .scv-podium {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px;
          max-width: 1180px; margin: 0 auto; padding: 0 24px;
        }
        @media (max-width: 980px) { .scv-podium { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 640px) { .scv-podium { grid-template-columns: 1fr; } }
        .scv-win { position: relative; }
        .scv-win .scv-card { border-width: 2px; }
        .scv-win.r1 .scv-card { border-color: #d4af37; box-shadow: 0 10px 30px rgba(212,175,55,0.28); }
        .scv-win.r2 .scv-card { border-color: #b8c0cc; }
        .scv-win.r3 .scv-card { border-color: #cd8c5c; }
        .scv-rank {
          display: inline-flex; align-items: center; gap: 8px;
          font-weight: 800; font-size: 15px; color: #1a1a2e;
        }
        .scv-medal { font-size: 22px; line-height: 1; }
        .scv-votes {
          margin-left: auto; font-weight: 800; font-size: 15px; color: #004d99;
          background: #eaf2fb; border: 1px solid #d4e4f6; padding: 4px 12px; border-radius: 999px;
          font-variant-numeric: tabular-nums;
        }
      `}</style>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #004d99, #0066cc)', color: '#fff', padding: '64px 24px 56px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ textTransform: 'uppercase', letterSpacing: '.18em', fontSize: 13, opacity: 0.85, marginBottom: 14 }}>
            International Research Olympiad
          </div>
          <h1 style={{ fontSize: 'clamp(30px, 5vw, 46px)', fontWeight: 800, margin: '0 0 18px', lineHeight: 1.1 }}>
            Public Science Communication Award
          </h1>
          {votingOpen ? (
            <>
              <p style={{ fontSize: 18, lineHeight: 1.6, opacity: 0.95, margin: 0 }}>
                Each finalist made a two-minute video explaining their science to the world.
                Watch them all and vote for the communicator who moved you most. The winner is
                chosen by you, the community.
              </p>
              <p style={{ fontSize: 14, marginTop: 22, opacity: 0.8 }}>
                One vote per person. You can&rsquo;t change your vote once it&rsquo;s cast.
              </p>
            </>
          ) : (
            <p style={{ fontSize: 18, lineHeight: 1.6, opacity: 0.95, margin: 0 }}>
              Voting has now closed. Thank you to everyone who took part &mdash;
              congratulations to our top five science communicators, chosen by the community.
            </p>
          )}
        </div>
      </div>

      {/* Status banner — either a thank-you, or an explanation of why the
          vote was blocked (network already used vs. this browser). */}
      {votingOpen && hasVoted && (
        <div style={{ maxWidth: 1180, margin: '28px auto 0', padding: '0 24px' }}>
          {voteReason === 'network' ? (
            <div style={{
              background: '#fff4e5', color: '#8a4b08', border: '1px solid #f3d49a',
              borderRadius: 12, padding: '16px 20px', textAlign: 'center', lineHeight: 1.55,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                We couldn&rsquo;t count your vote &mdash; this network has already voted.
              </div>
              A vote has already been registered from your internet connection. To keep the
              result fair, the award allows only <strong>one vote per network</strong>. If you
              share the same Wi-Fi or network as someone who has already voted, no further votes
              can be cast from it.
            </div>
          ) : (
            <div style={{
              background: '#e7f6ee', color: '#06713a', border: '1px solid #b9e6cd',
              borderRadius: 12, padding: '16px 20px', textAlign: 'center', fontWeight: 600,
            }}>
              ✓ Thanks &mdash; your vote has been counted. Results will be announced after voting closes.
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ maxWidth: 1180, margin: '20px auto 0', padding: '0 24px' }}>
          <div style={{ background: '#fdecec', color: '#b3261e', border: '1px solid #f5c2c0', borderRadius: 12, padding: '14px 18px', textAlign: 'center' }}>
            {error}
          </div>
        </div>
      )}

      {/* Winners reveal — shown once voting has closed (server-driven). */}
      {!loading && !votingOpen && (
        <div style={{ marginTop: 40 }}>
          {winners.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#687587' }}>Results will be posted shortly.</p>
          ) : (
            <div className="scv-podium">
              {winners.map((w) => {
                const embed = youtubeEmbed(w.youtube_url);
                const medal = w.rank === 1 ? '🥇' : w.rank === 2 ? '🥈' : w.rank === 3 ? '🥉' : '';
                const place = ['1st', '2nd', '3rd', '4th', '5th'][w.rank - 1] || `${w.rank}th`;
                return (
                  <div className={`scv-win r${w.rank}`} key={w.id}>
                    <div className="scv-card">
                      <div className="scv-video">
                        {embed ? (
                          <iframe
                            src={embed}
                            title={w.name || `Winner ${w.rank}`}
                            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <div className="scv-soon">
                            {w.photo_url ? (
                              <img className="scv-soon-photo" src={w.photo_url} alt={w.name || ''} />
                            ) : (
                              <div className="scv-soon-photo scv-soon-initials">{initials(w.name)}</div>
                            )}
                          </div>
                        )}
                      </div>
                      <div style={{ padding: '18px 18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span className="scv-rank">{medal && <span className="scv-medal">{medal}</span>}{place}</span>
                          <span className="scv-votes">{(w.votes || 0).toLocaleString()} votes</span>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 19, color: '#1a1a2e' }}>
                          {w.name || `Winner ${w.rank}`}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Grid */}
      {votingOpen && (
      <div style={{ marginTop: 40 }}>
        {loading ? (
          <p style={{ textAlign: 'center', color: '#687587' }}>Loading candidates&hellip;</p>
        ) : (
          <div className="scv-grid">
            {candidates.map((c) => {
              const embed = youtubeEmbed(c.youtube_url);
              const isVotedOne = votedId === c.id;
              const isPending = pendingId === c.id;
              return (
                <div className="scv-card" key={c.id}>
                  <div className="scv-video">
                    {embed ? (
                      <iframe
                        src={embed}
                        title={c.name || `Candidate ${c.id}`}
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div className="scv-soon">
                        {c.photo_url ? (
                          <img className="scv-soon-photo" src={c.photo_url} alt={c.name || ''} />
                        ) : (
                          <div className="scv-soon-photo scv-soon-initials">{initials(c.name)}</div>
                        )}
                        <span>Video coming soon</span>
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '18px 18px 20px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
                    <div className="scv-name-row">
                      {c.photo_url ? (
                        <img className="scv-avatar" src={c.photo_url} alt="" />
                      ) : (
                        <div className="scv-avatar scv-avatar-initials">{initials(c.name)}</div>
                      )}
                      <div style={{ fontWeight: 700, fontSize: 18, color: '#1a1a2e' }}>
                        {c.name || `Candidate ${c.id}`}
                      </div>
                    </div>
                    <div style={{ marginTop: 'auto' }}>
                      {isVotedOne ? (
                        <button className="scv-btn" disabled>Your vote ✓</button>
                      ) : hasVoted ? (
                        <button className="scv-btn" disabled>Voting closed for you</button>
                      ) : !embed ? (
                        <button className="scv-btn" disabled>Not available yet</button>
                      ) : isPending ? (
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button
                            className="scv-btn scv-confirm"
                            disabled={submitting}
                            onClick={() => castVote(c.id)}
                          >
                            {submitting ? 'Saving…' : 'Confirm vote'}
                          </button>
                          <button
                            className="scv-btn scv-cancel"
                            disabled={submitting}
                            onClick={() => setPendingId(null)}
                            style={{ width: 'auto', padding: '12px 18px' }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button className="scv-btn" onClick={() => setPendingId(c.id)}>
                          Vote for this
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
